import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import CheckoutContext from '../lib/CheckoutContext';
import { HARDWARE_FIRST_MAX_UNITS_PER_CART, hardwareFirstUnitCount } from '../lib/hardwareFirst';
import * as types from '../types';
import useHardwareFirstCheckout from './useHardwareFirstCheckout';

/**
 * Hardware-first units in the active checkout cart (0 when not enrolled). Gates the klarna/bnpl
 * options, the free-shipping display, and the per-cart unit cap. Must be called within a
 * CheckoutContextProvider.
 */
export default function useHardwareFirstCartUnits(): number {
  const enrolled = useHardwareFirstCheckout();
  const { cart } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);

  return useMemo(() => (enrolled ? hardwareFirstUnitCount(cart, products) : 0), [enrolled, cart, products]);
}

/**
 * Whether the persisted store cart is already at the hardware-first unit cap. For PDP add-to-bag
 * guards, which render outside a CheckoutContextProvider.
 */
export function useHardwareFirstStoreCartAtCap(): boolean {
  const enrolled = useHardwareFirstCheckout();
  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const products = useSelector((state: types.AppState) => state.config.products);

  return useMemo(
    () => enrolled && hardwareFirstUnitCount(cart, products) >= HARDWARE_FIRST_MAX_UNITS_PER_CART,
    [enrolled, cart, products],
  );
}
